import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, User } from "lucide-react";

const EMPTY = {
  company_id: "", name: "", email: "", phone: "", position: "", group: "", address: "", ssn_last4: "",
  hourly_rate: 0, salary_type: "hourly", annual_salary: 0, payment_method: "direct_deposit",
  bank_name: "", account_number: "", routing_number: "", check_address: "", avatar_url: "",
};

export default function Employees() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showAcct, setShowAcct] = useState(false);

  const load = useCallback(() => {
    const q = companyFilter !== "all" ? `?company_id=${companyFilter}` : "";
    api.get(`/employees${q}`).then((r) => setEmployees(r.data)).catch(() => {});
  }, [companyFilter]);

  const loadGroups = () => api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    loadGroups();
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const defaultCompany = user?.role === "manager" ? user.company_id : companies[0]?.id || "";
    setEditing(null);
    setForm({ ...EMPTY, company_id: defaultCompany });
    setShowAcct(false);
    loadGroups();
    setOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...EMPTY, ...emp });
    setShowAcct(false);
    loadGroups();
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, hourly_rate: Number(form.hourly_rate) || 0, annual_salary: Number(form.annual_salary) || 0 };
    if (!payload.company_id) return toast.error("Please select a company");
    try {
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Remove ${emp.name}?`)) return;
    await api.delete(`/employees/${emp.id}`);
    toast.success("Employee removed");
    load();
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">People</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Employees</h1>
          <p className="text-slate-500 mt-1">Personal details, pay rate and payment method.</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-48" data-testid="company-selector-dropdown"><SelectValue placeholder="All companies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={openNew} data-testid="add-employee-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><Plus className="w-4 h-4 mr-1" /> Add employee</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Employee</th>
              <th className="text-left font-semibold px-6 py-3">Company</th>
              <th className="text-left font-semibold px-6 py-3">Pay</th>
              <th className="text-left font-semibold px-6 py-3">Payment</th>
              <th className="text-right font-semibold px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {employees.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400">No employees yet. Add your first one.</td></tr>
            )}
            {employees.map((emp) => (
              <tr key={emp.id} data-testid={`employee-row-${emp.id}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {emp.avatar_url
                      ? <img src={emp.avatar_url} alt={emp.name} className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center"><User className="w-4 h-4" /></div>}
                    <div>
                      <p className="font-semibold text-slate-800">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.position || emp.email || "—"}</p>
                      {emp.group && <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-semibold uppercase tracking-wide">{emp.group}</span>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-600">{companyName(emp.company_id)}</td>
                <td className="px-6 py-4 font-mono text-slate-700">
                  {emp.salary_type === "salary" ? `${money(emp.annual_salary)}/yr` : `${money(emp.hourly_rate)}/hr`}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                    {emp.payment_method === "check" ? "Paper Check" : "Direct Deposit"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(emp)} data-testid={`edit-employee-${emp.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-sky-600 transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(emp)} data-testid={`delete-employee-${emp.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit employee" : "New employee"}</DialogTitle><DialogDescription>Enter personal details, pay rate, and payment method. Assign a group so they clock in under the right folder.</DialogDescription></DialogHeader>
          <form onSubmit={save}>
            <Tabs defaultValue="personal">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
                <TabsTrigger value="pay" data-testid="tab-pay">Pay</TabsTrigger>
                <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
              </TabsList>

              <TabsContent value="personal" className="space-y-4 mt-4">
                {user?.role === "admin" && (
                  <div>
                    <Label>Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="employee-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Full name</Label><Input data-testid="employee-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Position</Label><Input data-testid="employee-position-input" value={form.position} onChange={(e) => set("position", e.target.value)} className="mt-1.5" /></div>
                  <div>
                    <Label>Group / Shift</Label>
                    <Select value={form.group ? form.group : "__none__"} onValueChange={(v) => set("group", v === "__none__" ? "" : v)}>
                      <SelectTrigger className="mt-1.5" data-testid="employee-group-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {groups.filter((g) => g.company_id === form.company_id).map((g) => (
                          <SelectItem key={g.id} value={g.name} data-testid={`group-option-${g.name.replace(/\s+/g, "-").toLowerCase()}`}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {groups.filter((g) => g.company_id === form.company_id).length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No groups for this company yet — create them on the Time Tracker page.</p>
                    )}
                  </div>
                  <div><Label>Email</Label><Input data-testid="employee-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
                  <div><Label>Phone</Label><Input data-testid="employee-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
                  <div className="col-span-2"><Label>Address</Label><Input data-testid="employee-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" /></div>
                  <div><Label>SSN (last 4)</Label><Input data-testid="employee-ssn-input" maxLength={4} value={form.ssn_last4} onChange={(e) => set("ssn_last4", e.target.value)} className="mt-1.5" /></div>
                </div>
              </TabsContent>

              <TabsContent value="pay" className="space-y-4 mt-4">
                <div>
                  <Label>Salary type</Label>
                  <Select value={form.salary_type} onValueChange={(v) => set("salary_type", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="salary-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="salary">Salaried (annual)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.salary_type === "hourly" ? (
                  <div><Label>Hourly rate ($)</Label><Input data-testid="hourly-rate-input" type="number" step="0.01" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="mt-1.5 font-mono" /></div>
                ) : (
                  <div><Label>Annual salary ($)</Label><Input data-testid="annual-salary-input" type="number" step="0.01" value={form.annual_salary} onChange={(e) => set("annual_salary", e.target.value)} className="mt-1.5 font-mono" /></div>
                )}
                <p className="text-xs text-slate-500">Overtime beyond 40 hrs/week is auto-computed at 1.5× for hourly employees.</p>
              </TabsContent>

              <TabsContent value="payment" className="space-y-4 mt-4">
                <div>
                  <Label>Payment method</Label>
                  <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                      <SelectItem value="check">Paper Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.payment_method === "direct_deposit" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2"><Label>Bank name</Label><Input data-testid="bank-name-input" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} className="mt-1.5" /></div>
                    <div>
                      <Label>Account number</Label>
                      <Input data-testid="account-number-input" type={showAcct ? "text" : "password"} value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="mt-1.5 font-mono" />
                    </div>
                    <div><Label>Routing number</Label><Input data-testid="routing-number-input" value={form.routing_number} onChange={(e) => set("routing_number", e.target.value)} className="mt-1.5 font-mono" /></div>
                    <button type="button" onClick={() => setShowAcct((s) => !s)} data-testid="toggle-account-visibility" className="col-span-2 text-left text-xs font-semibold text-sky-600 hover:underline">{showAcct ? "Hide" : "Show"} account number</button>
                  </div>
                ) : (
                  <div><Label>Check mailing address</Label><Input data-testid="check-address-input" value={form.check_address} onChange={(e) => set("check_address", e.target.value)} className="mt-1.5" /></div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="submit" data-testid="save-employee-button" className="bg-sky-600 hover:bg-sky-700">{editing ? "Update" : "Add employee"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
